/**
 * Query keys de operations — operativa: CRM, contactos, productos, proyectos y tareas.
 *
 * Se componen en `src/lib/qk.ts`, que sigue siendo el único punto de import de
 * toda la app (`@/lib/qk`). Aquí solo vive la definición; añadir una clave nueva
 * de este dominio se hace en este fichero y aparece sola en `qk`.
 *
 * Las convenciones (entidad en singular primero, `companyId` después, subsegmentos
 * en medio) están explicadas en `src/lib/qk.ts` y siguen aplicando.
 */
export const operationsKeys = {
  contact: {
    all: () => ['contacts'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['contacts', companyId, filters] as const,
  },

  // Directorio unificado clientes+proveedores con Tipo derivado (prereq #931).
  contactDirectory: {
    all: () => ['contact-directory'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['contact-directory', companyId, filters] as const,
  },

  // Ficha unificada de contacto (todos los roles, /api/business-contacts) — fuente
  // de los selectores de tercero del alta de gasto/factura (rol derivado del movimiento).
  businessContact: {
    all: () => ['business-contacts'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['business-contacts', companyId, filters] as const,
  },

  // PERSONAS multi-empresa — vínculos persona↔tercero (/api/contact-links).
  contactBusinessContactLink: {
    all: () => ['contact-links'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['contact-links', companyId, filters] as const,
  },

  // PERSONAS multi-empresa — directorio transversal de personas (/api/contact-people-directory).
  contactPeopleDirectory: {
    all: () => ['contact-people-directory'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['contact-people-directory', companyId, filters] as const,
  },

  deal: {
    all: () => ['deals'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['deals', companyId, filters] as const,
    dashboard: (companyId?: string) => ['deals', 'dashboard', companyId] as const,
    segmentation: (companyId?: string) => ['deals', 'segmentation', companyId] as const,
  },

  // ── CRM multi-embudo — catálogo de pipelines por empresa (GET /api/pipelines).
  // El backend auto-siembra 7 etapas al crear un pipeline.
  pipeline: {
    all: () => ['pipelines'] as const,
    list: (companyId?: string) => ['pipelines', companyId] as const,
  },

  // ── CRM motivos de pérdida — catálogo por empresa (/api/deal-lost-reasons).
  dealLostReason: {
    all: () => ['deal-lost-reasons'] as const,
    list: (companyId?: string) => ['deal-lost-reasons', companyId] as const,
  },

  // ── CRM Fase D — catálogo de campos personalizados (/api/crm-custom-field-definitions).
  crmCustomFieldDefinition: {
    all: () => ['crm-custom-field-definitions'] as const,
    list: (companyId?: string) => ['crm-custom-field-definitions', companyId] as const,
  },

  // ── CRM Fase E — equipos de venta y su membresía.
  salesTeam: {
    all: () => ['sales-teams'] as const,
    list: (companyId?: string) => ['sales-teams', companyId] as const,
  },

  salesTeamMember: {
    all: () => ['sales-team-members'] as const,
    list: (companyId?: string, teamId?: string) => ['sales-team-members', companyId, teamId] as const,
  },

  // ── CRM Fase C — secuencias de email reales (/api/crm-sequences).
  crmSequence: {
    all: () => ['crm-sequences'] as const,
    list: (companyId?: string) => ['crm-sequences', companyId] as const,
  },

  // ── CRM Fase C — inscripciones en secuencias (/api/crm-sequence-enrollments).
  crmSequenceEnrollment: {
    all: () => ['crm-sequence-enrollments'] as const,
    list: (companyId?: string) => ['crm-sequence-enrollments', companyId] as const,
  },

  product: {
    all: () => ['products'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['products', companyId, filters] as const,
  },

  project: {
    all: () => ['projects'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['projects', companyId, filters] as const,
    resources: (companyId?: string) => ['projects', 'resources', companyId] as const,
    portal: (companyId?: string) => ['projects', 'portal', companyId] as const,
    forExpenseForm: (companyId?: string) => ['projects', 'expense-form', companyId] as const,
  },

  task: {
    all: () => ['tasks'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['tasks', companyId, filters] as const,
    dashboard: (companyId?: string, projectIds?: string) =>
      ['tasks', 'dashboard', companyId, projectIds] as const,
    forMilestone: (projectId?: string) => ['tasks', 'milestone', projectId] as const,
  },
} as const;
